/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ESCAPE} from '@angular/cdk/keycodes';
import {
  BlockScrollStrategy,
  Overlay,
  OverlayRef,
  OverlayConfig,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import {ComponentPortal, ComponentType, TemplatePortal} from '@angular/cdk/portal';
import {startWith} from '@angular/cdk/rxjs';
import {Location} from '@angular/common';
import {
  ComponentRef,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  Optional,
  SkipSelf,
  TemplateRef,
} from '@angular/core';
import {extendObject, PortalInjector} from '@angular/material/core';
import {Observable} from 'rxjs/Observable';
import {defer} from 'rxjs/observable/defer';
import {Subject} from 'rxjs/Subject';
import {MdDialogConfig} from './dialog-config';
import {MdDialogContainer} from './dialog-container';
import {MdDialogRef} from './dialog-ref';

export const /** @type {?} */ MD_DIALOG_DATA = new InjectionToken<any>('MdDialogData');
/**
 * Injection token that determines the scroll handling while the dialog is open.
 */
export const MD_DIALOG_SCROLL_STRATEGY =
    new InjectionToken<() => ScrollStrategy>('md-dialog-scroll-strategy');
/**
 * \@docs-private
 * @param {?} overlay
 * @return {?}
 */
export function MD_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay):
    () => BlockScrollStrategy {
  return () => overlay.scrollStrategies.block();
}
/**
 * \@docs-private
 */
export const MD_DIALOG_SCROLL_STRATEGY_PROVIDER = {
  provide: MD_DIALOG_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: MD_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY,
};
/**
 * Service to open Material Design modal dialogs.
 */
export class MdDialog {
private _openDialogsAtThisLevel: MdDialogRef<any>[] = [];
private _afterAllClosedAtThisLevel = new Subject<void>();
private _afterOpenAtThisLevel = new Subject<MdDialogRef<any>>();
private _boundKeydown = this._handleKeydown.bind(this);
/**
 * Keeps track of the currently-open dialogs.
 * @return {?}
 */
get openDialogs(): MdDialogRef<any>[] {
    return this._parentDialog ? this._parentDialog.openDialogs : this._openDialogsAtThisLevel;
  }
/**
 * Stream that emits when a dialog has been opened.
 * @return {?}
 */
get afterOpen(): Subject<MdDialogRef<any>> {
    return this._parentDialog ? this._parentDialog.afterOpen : this._afterOpenAtThisLevel;
  }
/**
 * @return {?}
 */
get _afterAllClosed() {
    const /** @type {?} */ parent = this._parentDialog;
    return parent ? parent._afterAllClosed : this._afterAllClosedAtThisLevel;
  }
/**
 * Stream that emits when all open dialog have finished closing.
 * Will emit on subscribe if there are no open dialogs to begin with.
 */
afterAllClosed: Observable<void> = defer<void>(() => this.openDialogs.length ?
      this._afterAllClosed :
      startWith.call(this._afterAllClosed, undefined));
/**
 * @param {?} _overlay
 * @param {?} _injector
 * @param {?} location
 * @param {?} _scrollStrategy
 * @param {?} _parentDialog
 */
constructor(
private _overlay: Overlay,
private _injector: Injector,
       location: Location,
private _scrollStrategy,
private _parentDialog: MdDialog) {

    // Close all of the dialogs when the user goes forwards/backwards in history or when the
    // location hash changes. Note that this usually doesn't include clicking on links (unless
    // the user is using the `HashLocationStrategy`).
    if (!_parentDialog && location) {
      location.subscribe(() => this.closeAll());
    }
  }
/**
 * Opens a modal dialog containing the given component.
 * @template T
 * @param {?} componentOrTemplateRef Type of the component to load into the dialog,
 *     or a TemplateRef to instantiate as the dialog content.
 * @param {?=} config Extra configuration options.
 * @return {?} Reference to the newly-opened dialog.
 */
open<T>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
          config?: MdDialogConfig): MdDialogRef<T> {

    const /** @type {?} */ inProgressDialog = this.openDialogs.find(dialog => dialog._isAnimating());

    // If there's a dialog that is in the process of being opened, return it instead.
    if (inProgressDialog) {
      return inProgressDialog;
    }

    config = _applyConfigDefaults(config);

    if (config.id && this.getDialogById(config.id)) {
      throw Error(`Dialog with id "${config.id}" exists already. The dialog id must be unique.`);
    }

    const /** @type {?} */ overlayRef = this._createOverlay(config);
    const /** @type {?} */ dialogContainer = this._attachDialogContainer(overlayRef, config);
    const /** @type {?} */ dialogRef =
        this._attachDialogContent(componentOrTemplateRef, dialogContainer, overlayRef, config);

    if (!this.openDialogs.length) {
      document.addEventListener('keydown', this._boundKeydown);
    }

    this.openDialogs.push(dialogRef);
    dialogRef.afterClosed().subscribe(() => this._removeOpenDialog(dialogRef));
    this.afterOpen.next(dialogRef);

    return dialogRef;
  }
/**
 * Closes all of the currently-open dialogs.
 * @return {?}
 */
closeAll(): void {
    let /** @type {?} */ i = this.openDialogs.length;

    while (i--) {
      // The `_openDialogs` property isn't updated after close until the rxjs subscription
      // runs on the next microtask, in addition to modifying the array as we're going
      // through it. We loop through all of them and call close without assuming that
      // they'll be removed from the list instantaneously.
      this.openDialogs[i].close();
    }
  }
/**
 * Finds an open dialog by its id.
 * @param {?} id ID to use when looking up the dialog.
 * @return {?}
 */
getDialogById(id: string): MdDialogRef<any> | undefined {
    return this.openDialogs.find(dialog => dialog.id === id);
  }
/**
 * Creates the overlay into which the dialog will be loaded.
 * @param {?} config The dialog configuration.
 * @return {?} A promise resolving to the OverlayRef for the created overlay.
 */
private _createOverlay(config: MdDialogConfig): OverlayRef {
    const /** @type {?} */ overlayState = this._getOverlayState(config);
    return this._overlay.create(overlayState);
  }
/**
 * Creates an overlay state from a dialog config.
 * @param {?} dialogConfig The dialog configuration.
 * @return {?} The overlay configuration.
 */
private _getOverlayState(dialogConfig: MdDialogConfig): OverlayConfig {
    const /** @type {?} */ state = new OverlayConfig({
      positionStrategy: this._overlay.position().global(),
      scrollStrategy: this._scrollStrategy(),
      panelClass: dialogConfig.panelClass,
      hasBackdrop: dialogConfig.hasBackdrop,
      direction: dialogConfig.direction
    });

    if (dialogConfig.backdropClass) {
      state.backdropClass = dialogConfig.backdropClass;
    }

    return state;
  }
/**
 * Attaches an MdDialogContainer to a dialog's already-created overlay.
 * @param {?} overlay Reference to the dialog's underlying overlay.
 * @param {?} config The dialog configuration.
 * @return {?} A promise resolving to a ComponentRef for the attached container.
 */
private _attachDialogContainer(overlay: OverlayRef, config: MdDialogConfig): MdDialogContainer {
    let /** @type {?} */ containerPortal = new ComponentPortal(MdDialogContainer, config.viewContainerRef);
    let /** @type {?} */ containerRef: ComponentRef<MdDialogContainer> = overlay.attach(containerPortal);
    containerRef.instance._config = config;

    return containerRef.instance;
  }
/**
 * Attaches the user-provided component to the already-created MdDialogContainer.
 * @template T
 * @param {?} componentOrTemplateRef The type of component being loaded into the dialog,
 *     or a TemplateRef to instantiate as the content.
 * @param {?} dialogContainer Reference to the wrapping MdDialogContainer.
 * @param {?} overlayRef Reference to the overlay in which the dialog resides.
 * @param {?} config The dialog configuration.
 * @return {?} A promise resolving to the MdDialogRef that should be returned to the user.
 */
private _attachDialogContent<T>(
      componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
      dialogContainer: MdDialogContainer,
      overlayRef: OverlayRef,
      config: MdDialogConfig): MdDialogRef<T> {

    // Create a reference to the dialog we're creating in order to give the user a handle
    // to modify and close it.
    const /** @type {?} */ dialogRef = new MdDialogRef<T>(overlayRef, dialogContainer, config.id);

    // When the dialog backdrop is clicked, we want to close it.
    if (config.hasBackdrop) {
      overlayRef.backdropClick().subscribe(() => {
        if (!dialogRef.disableClose) {
          dialogRef.close();
        }
      });
    }

    if (componentOrTemplateRef instanceof TemplateRef) {
      dialogContainer.attachTemplatePortal(
        new TemplatePortal<T>(componentOrTemplateRef, /** @type {?} */(( null)), /** @type {?} */((
          <any>{ $implicit: config.data, dialogRef }))));
    } else {
      const /** @type {?} */ injector = this._createInjector<T>(config, dialogRef, dialogContainer);
      const /** @type {?} */ contentRef = dialogContainer.attachComponentPortal(
          new ComponentPortal(componentOrTemplateRef, undefined, injector));
      dialogRef.componentInstance = contentRef.instance;
    }

    dialogRef
      .updateSize(config.width, config.height)
      .updatePosition(config.position);

    return dialogRef;
  }
/**
 * Creates a custom injector to be used inside the dialog. This allows a component loaded inside
 * of a dialog to close itself and, optionally, to return a value.
 * @template T
 * @param {?} config Config object that is used to construct the dialog.
 * @param {?} dialogRef Reference to the dialog.
 * @param {?} dialogContainer
 * @return {?} The custom injector that can be used inside the dialog.
 */
private _createInjector<T>(
      config: MdDialogConfig,
      dialogRef: MdDialogRef<T>,
      dialogContainer: MdDialogContainer): PortalInjector {

    const /** @type {?} */ userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
    const /** @type {?} */ injectionTokens = new WeakMap();

    injectionTokens.set(MdDialogRef, dialogRef);
    injectionTokens.set(MdDialogContainer, dialogContainer);
    injectionTokens.set(MD_DIALOG_DATA, config.data);

    return new PortalInjector(userInjector || this._injector, injectionTokens);
  }
/**
 * Removes a dialog from the array of open dialogs.
 * @param {?} dialogRef Dialog to be removed.
 * @return {?}
 */
private _removeOpenDialog(dialogRef: MdDialogRef<any>) {
    const /** @type {?} */ index = this.openDialogs.indexOf(dialogRef);

    if (index > -1) {
      this.openDialogs.splice(index, 1);

      // no open dialogs are left, call next on afterAllClosed Subject
      if (!this.openDialogs.length) {
        this._afterAllClosed.next();
        document.removeEventListener('keydown', this._boundKeydown);
      }
    }
  }
/**
 * Handles global key presses while there are open dialogs. Closes the
 * top dialog when the user presses escape.
 * @param {?} event
 * @return {?}
 */
private _handleKeydown(event: KeyboardEvent): void {
    const /** @type {?} */ topDialog = this.openDialogs[this.openDialogs.length - 1];
    const /** @type {?} */ canClose = topDialog ? !topDialog.disableClose : false;

    if (event.keyCode === ESCAPE && canClose) {
      topDialog.close();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Overlay, },
{type: Injector, },
{type: Location, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Inject, args: [MD_DIALOG_SCROLL_STRATEGY, ] }, ]},
{type: MdDialog, decorators: [{ type: Optional }, { type: SkipSelf }, ]},
];
}

function MdDialog_tsickle_Closure_declarations() {
/** @type {?} */
MdDialog.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDialog.ctorParameters;
/** @type {?} */
MdDialog.prototype._openDialogsAtThisLevel;
/** @type {?} */
MdDialog.prototype._afterAllClosedAtThisLevel;
/** @type {?} */
MdDialog.prototype._afterOpenAtThisLevel;
/** @type {?} */
MdDialog.prototype._boundKeydown;
/**
 * Stream that emits when all open dialog have finished closing.
 * Will emit on subscribe if there are no open dialogs to begin with.
 * @type {?}
 */
MdDialog.prototype.afterAllClosed;
/** @type {?} */
MdDialog.prototype._overlay;
/** @type {?} */
MdDialog.prototype._injector;
/** @type {?} */
MdDialog.prototype._scrollStrategy;
/** @type {?} */
MdDialog.prototype._parentDialog;
}

/**
 * Applies default options to the dialog config.
 * @param {?=} config Config to be modified.
 * @return {?} The new configuration object.
 */
function _applyConfigDefaults(config?: MdDialogConfig): MdDialogConfig {
  return extendObject(new MdDialogConfig(), config);
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
